import React, {useState, useEffect} from 'react';
import {View, StyleSheet, ScrollView, Pressable, ActivityIndicator} from 'react-native';
import SlideModal from '@/Atoms/SlideModal';
import Box from '@/Atoms/Box';
import CCText from '@/Atoms/CCText';
import CCButton from '@/Atoms/CCButton';
import CCColors from '@/Utils/CCColors';
import {FontSizes, vs, hs} from '@/Utils/Dimensions';
import {MaterialCommunityIcons} from '@expo/vector-icons';
import Spacer from '@/Atoms/Spacer';
import dayjs from 'dayjs';
import {useMediaQuery} from '@/Hooks/useMediaQuery/useMediaQuery';
import useGetAvailableSmallGroupBatches, {AvailableBatch} from '@/Hooks/RenewalApi/useGetAvailableSmallGroupBatches';
import CCLoader from '@/Atoms/CCLoader';

export interface SelectedBatch {
  batch_name: string;
  batch_code: string;
  coach_name: string;
  class_timing: string;
  class_days: string;
  start_date: string;
  tournament_id: number;
  level_name: string | null;
}

interface BatchSelectionModalProps {
  visible: boolean;
  onClose: () => void;
  onSelect: (batch: SelectedBatch | null) => void;
  selectedBatch?: SelectedBatch | null;
  tournamentId?: number;
  planExpiryDate?: string;
  onOpenCustomTiming?: () => void;
  currentCoachName?: string | null; // Current or past coach name to show tag
}

const BatchSelectionModal: React.FC<BatchSelectionModalProps> = ({
  visible,
  onClose,
  onSelect,
  selectedBatch: initialSelectedBatch = null,
  tournamentId,
  planExpiryDate,
  onOpenCustomTiming,
  currentCoachName,
}) => {
  const [selectedBatch, setSelectedBatch] = useState<SelectedBatch | null>(initialSelectedBatch);
  const {isMobileOrTab} = useMediaQuery();

  const {
    data: batchesData,
    isLoading,
    error,
  } = useGetAvailableSmallGroupBatches(tournamentId, planExpiryDate, visible && !!tournamentId);

  const batches = batchesData?.batches || [];
  // Get current coach name from API response (preferred) or fallback to prop
  const currentCoachNameFromApi = batchesData?.current_coach_name;
  const effectiveCurrentCoachName = currentCoachNameFromApi !== undefined ? currentCoachNameFromApi : currentCoachName;

  useEffect(() => {
    if (initialSelectedBatch) {
      setSelectedBatch(initialSelectedBatch);
    }
  }, [initialSelectedBatch]);

  useEffect(() => {
    if (!visible) {
      // Don't clear selectedBatch on close - keep it for next open
    }
  }, [visible]);

  const handleBatchSelect = (batch: AvailableBatch) => {
    const batchToSelect: SelectedBatch = {
      batch_name: batch.batch_name,
      batch_code: batch.batch_code,
      coach_name: batch.coach_name,
      class_timing: batch.class_timing,
      class_days: batch.class_days,
      start_date: batch.start_date,
      tournament_id: batch.tournament_id,
      level_name: batch.level_name,
    };
    setSelectedBatch(batchToSelect);
  };

  const handleSubmit = () => {
    if (selectedBatch) {
      onSelect(selectedBatch);
    }
  };

  const handleDidntFindSlot = () => {
    if (onOpenCustomTiming) {
      onOpenCustomTiming();
    }
  };

  const formatClassDays = (classDaysStr: string): string => {
    if (!classDaysStr) return '';
    const dayMap: {[key: string]: string} = {
      '1': 'Mon',
      '2': 'Tue',
      '3': 'Wed',
      '4': 'Thu',
      '5': 'Fri',
      '6': 'Sat',
      '7': 'Sun',
    };
    const days = classDaysStr.split(',').map(d => d.trim()).filter(Boolean);
    return days.map(d => dayMap[d] || d).join(', ');
  };

  const formatClassTiming = (classTimingStr: string): string => {
    if (!classTimingStr) return '';
    return classTimingStr.split(',').map(t => t.trim()).join(', ');
  };

  // Format day-time combination: maps days to timings (first day to first time, etc.)
  // If fewer timings than days, last timing is used for remaining days
  // Groups consecutive days with the same timing together (e.g., "Mon, Tue 7pm")
  const formatDayTime = (classDaysStr: string, classTimingStr: string): string => {
    if (!classDaysStr) return '';
    
    const dayMap: {[key: string]: string} = {
      '1': 'Mon',
      '2': 'Tue',
      '3': 'Wed',
      '4': 'Thu',
      '5': 'Fri',
      '6': 'Sat',
      '7': 'Sun',
    };
    
    const days = classDaysStr.split(',').map(d => d.trim()).filter(Boolean);
    const timings = classTimingStr ? classTimingStr.split(',').map(t => t.trim()).filter(Boolean) : [];
    
    if (timings.length === 0) {
      // No timings, just return days
      return days.map(d => dayMap[d] || d).join(', ');
    }
    
    // Map each day to its corresponding time
    // If there are more days than timings, use the last timing for remaining days
    const dayTimePairs = days.map((day, index) => {
      const dayName = dayMap[day] || day;
      const dayNum = parseInt(day);
      // Use the time at the same index, or the last time if index exceeds timings length
      const timeIndex = Math.min(index, timings.length - 1);
      const time = timings[timeIndex];
      return { day: dayName, dayNum, time };
    });
    
    // Group ALL days with the same timing together (not just consecutive)
    const timingGroups: { [time: string]: string[] } = {};
    
    for (const pair of dayTimePairs) {
      if (!timingGroups[pair.time]) {
        timingGroups[pair.time] = [];
      }
      timingGroups[pair.time].push(pair.day);
    }
    
    // Format groups: "Day1, Day2, ... Time" or "Day Time"
    return Object.entries(timingGroups)
      .map(([time, dayList]) => {
        if (dayList.length === 1) {
          return `${dayList[0]} ${time}`;
        } else {
          return `${dayList.join(', ')} ${time}`;
        }
      })
      .join(', ');
  };

  const formatStartDate = (dateStr: string): string => {
    if (!dateStr) return '';
    try {
      return dayjs(dateStr).format('MMM D, YYYY');
    } catch {
      return dateStr;
    }
  };

  return (
    <SlideModal
      visible={visible}
      showHeader
      showCloseIcon
      onClose={onClose}
      
      type={isMobileOrTab ? "bottom" : "center"}
      customStyle={{
        width: isMobileOrTab ? '50vw' : '80vw',
        height: !isMobileOrTab ? '95vh' : '85vh',
        paddingHorizontal: 0,
      }}
      modalHeaderTitle={
        <Box type="row-center" gap={3}>
          <MaterialCommunityIcons name="account-group" size={18} color={CCColors.TextColor.Dark} />
          <CCText fontSize={FontSizes[16]} fontWeight="semibold">
            Select Batch
          </CCText>
          <CCText fontSize={FontSizes[12]} color={CCColors.Red} fontWeight="semibold" marginLeft={8}>
            (Required)
          </CCText>
        </Box>
      }>
      <Box style={styles.modalContainer}>
        {/* Scrollable Content Area */}
        <ScrollView 
          style={styles.scrollableContent}
          contentContainerStyle={styles.scrollableContentContainer}
          showsVerticalScrollIndicator={true}>
          <Box>
            {/* Mandatory Selection Notice */}
            <Box
              backgroundColor={CCColors.Primary.lightYellow}
              padding={12}
              borderRadius={8}
              marginBottom={15}
              borderLeftWidth={4}
              borderLeftColor={CCColors.Primary.Default}>
              <Box flexDirection="row" alignItems="flex-start" gap={8}>
                <MaterialCommunityIcons
                  name="information"
                  size={18}
                  color={CCColors.Primary.Dark}
                />
                <Box flex={1}>
                  <CCText
                    fontSize={FontSizes[12]}
                    fontWeight="semibold"
                    color={CCColors.Primary.Dark}
                    marginBottom={2}>
                    Selection Required
                  </CCText>
                  <CCText fontSize={FontSizes[11]} color={CCColors.TextColor.Dark} lineHeight={16}>
                    Please select a batch or choose custom date/time slots to proceed with payment.
                  </CCText>
                </Box>
              </Box>
            </Box>

            {isLoading ? (
              <Box alignItems="center" justifyContent="center" paddingVertical={40}>
                <CCLoader loading={isLoading} isComponentLoader />
              </Box>
            ) : error ? (
              <Box alignItems="center" paddingVertical={40}>
                <CCText fontSize={FontSizes[14]} color={CCColors.Red}>
                  Failed to load batches. Please try again.
                </CCText>
              </Box>
            ) : batches.length === 0 ? (
              <Box alignItems="center" paddingVertical={40}>
                <CCText fontSize={FontSizes[14]} color={CCColors.TextColor.Grey} textAlign="center">
                  No batches available at this time.
                </CCText>
                {onOpenCustomTiming && (
                  <Box marginTop={20}>
                    <CCButton
                      onPress={handleDidntFindSlot}
                      style={styles.customTimingButton}
                      contentContainerStyle={styles.customTimingButtonContent}>
                      <CCText fontSize={FontSizes[14]} fontWeight="semibold" color={CCColors.Primary.Default}>
                        Didn't find a slot?
                      </CCText>
                    </CCButton>
                  </Box>
                )}
              </Box>
            ) : (
              <>
                {/* Batch Display - Compact Chip Layout for both Mobile and Desktop */}
                <Box style={styles.batchChipsContainer}>
                    {batches.map((batch, index) => {
                      const isSelected = selectedBatch?.batch_name === batch.batch_name;
                      const isCurrentCoach = effectiveCurrentCoachName && batch.coach_name && 
                        effectiveCurrentCoachName.toLowerCase().trim() === batch.coach_name.toLowerCase().trim();
                      const dayTimeStr = formatDayTime(batch.class_days || '', batch.class_timing || '');
                    
                    return (
                      <Pressable
                        key={`${batch.batch_name}-${index}`}
                        onPress={() => handleBatchSelect(batch)}
                        style={[
                          styles.batchChip,
                          isSelected && styles.batchChipSelected,
                          isCurrentCoach && styles.batchChipCurrentCoach,
                          isMobileOrTab && styles.batchChipMobile,
                        ]}>
                        {/* Top Row: Tag (if current coach) */}
                        {isCurrentCoach && (
                          <Box style={styles.currentCoachTag}>
                            <CCText
                              fontSize={FontSizes[10]}
                              fontWeight="bold"
                              color={CCColors.White}>
                              Current coach
                            </CCText>
                          </Box>
                        )}

                        {/* Main Content: Day-Time */}
                        <Box marginBottom={6}>
                          <CCText
                            fontSize={FontSizes[13]}
                            fontWeight="semibold"
                            color={isSelected ? CCColors.White : CCColors.TextColor.Dark}
                            numberOfLines={2}>
                            {dayTimeStr || 'Schedule TBD'}
                          </CCText>
                        </Box>

                        {/* Coach Name */}
                        {batch.coach_name && (
                          <Box flexDirection="row" alignItems="center" gap={4} marginBottom={6}>
                            <MaterialCommunityIcons
                              name="account"
                              size={14}
                              color={isSelected ? CCColors.White : CCColors.TextColor.Grey}
                            />
                            <CCText
                              fontSize={FontSizes[11]}
                              color={isSelected ? CCColors.White : CCColors.TextColor.Grey}
                              numberOfLines={1}>
                              {batch.coach_name}
                            </CCText>
                          </Box>
                        )}

                        {/* Bottom Row: Start Date */}
                        <Box flexDirection="row" alignItems="center" gap={4}>
                          <MaterialCommunityIcons
                            name="calendar-start"
                            size={14}
                            color={isSelected ? CCColors.White : CCColors.TextColor.Grey}
                          />
                          <CCText
                            fontSize={FontSizes[11]}
                            color={isSelected ? CCColors.White : CCColors.TextColor.Grey}>
                            Starts {batch.start_date ? formatStartDate(batch.start_date) : 'TBD'}
                          </CCText>
                        </Box>
                      </Pressable>
                    );
                  })}
                </Box>
              </>
            )}
          </Box>
        </ScrollView>

        {/* Fixed Bottom Action Buttons */}
        <Box style={isMobileOrTab ? styles.fixedBottomButtonsMobile : styles.fixedBottomButtons}>
          <Box type={isMobileOrTab ? "column-flex-between" : "row-center-between"}>
            {onOpenCustomTiming && (
              <Box marginTop={10}  alignItems={isMobileOrTab ? "center" : "flex-end"} marginBottom={isMobileOrTab ? 10 : 0}>
                <Pressable onPress={handleDidntFindSlot}>
                  <CCText fontSize={FontSizes[12]} color={CCColors.BlueAqua} fontWeight="semibold">
                    Didn't find a slot?
                  </CCText>
                </Pressable>
              </Box>
            )}
            <Box  type="row-center-between"  gap={10}>
              <CCButton
              parentStyle={isMobileOrTab ? styles.footerInnerContainerMobileButton : {}}
                onPress={onClose}
                style={[styles.cancelButton]}>
                <CCText fontSize={FontSizes[16]} fontWeight="semibold">
                  Cancel
                </CCText>
              </CCButton>
              <CCButton
              parentStyle={isMobileOrTab ? styles.footerInnerContainerMobileButton : {}}
              onPress={handleSubmit}
                disabled={!selectedBatch}
                style={[
                  styles.selectButton,
                  !selectedBatch && styles.selectButtonDisabled,
                ]}
                contentContainerStyle={styles.selectButtonContent}>
                <CCText
                  fontSize={FontSizes[16]}
                  fontWeight="semibold"
                  color={selectedBatch ? CCColors.TextColor.Dark : CCColors.TextColor.Grey}>
                  Select Batch
                </CCText>
              </CCButton>
            </Box>
          </Box>
        </Box>
      </Box>
    </SlideModal>
  );
};

const styles = StyleSheet.create({
  footerInnerContainerMobileButton: {
    flex:1
    // gap: 10,
  },
  modalContainer: {
    flex: 1,
    flexDirection: 'column',
  },
  scrollableContent: {
    flex: 0.5,
  },
  scrollableContentContainer: {
    flexGrow: 1,
  },
  
  // Batch Chip Styles (for both mobile and desktop)
  batchChipsContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
    marginBottom: 15,
  },
  batchChip: {
    flex: 1,
    minWidth: 200,
    maxWidth: 280,
    padding: 14,
    backgroundColor: CCColors.White,
    borderRadius: 10,
    borderWidth: 2,
    borderColor: CCColors.BorderBottom,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.08,
    shadowRadius: 4,
    elevation: 2,
    position: 'relative',
  },
  batchChipSelected: {
    backgroundColor: CCColors.Primary.Dark,
    borderColor: CCColors.Primary.Default,
    shadowColor: CCColors.Primary.Default,
    shadowOpacity: 0.3,
    elevation: 4,
  },
  batchChipCurrentCoach: {
    borderColor: CCColors.Primary.Aqua,
  },
  batchChipMobile: {
    minWidth: '48%',
    maxWidth: '48%',
    flexBasis: '48%',
  },
  currentCoachTag: {
    backgroundColor: CCColors.Primary.Aqua,
    borderRadius: 4,
    paddingHorizontal: 8,
    paddingVertical: 4,
    position: 'absolute',
    top: -8,
    right: -8,
    zIndex: 1,
  },
  
  // Button Styles
  fixedBottomButtons: {
    paddingHorizontal: 20,
    paddingVertical: 15,
    borderTopWidth: 1,
    borderTopColor: CCColors.BorderBottom,
    backgroundColor: CCColors.White,
    width: '100%',
  },
  fixedBottomButtonsMobile: {
    // paddingHorizontal: 12,
    // paddingVertical: 12,
    borderTopWidth: 1,
    borderTopColor: CCColors.BorderBottom,
    backgroundColor: CCColors.White,
    width: '100%',
  },
  cancelButton: {
    flex: 1,
    backgroundColor: CCColors.Grey.Border,
    borderRadius: 8,
    width: '100%',
    paddingVertical: vs(12),
  },
  selectButton: {
    flex: 0.5,
    backgroundColor: CCColors.Primary.Default,
    width: '100%',
    borderRadius: 8,
    paddingVertical: 12,
  },
  selectButtonDisabled: {
    opacity: 0.5,
  },
  selectButtonContent: {
    padding: 0,
  },
  customTimingButton: {
    backgroundColor: CCColors.White,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: CCColors.Primary.Default,
    paddingVertical: 10,
    paddingHorizontal: 20,
  },
  customTimingButtonContent: {
    padding: 0,
  },
});

export default BatchSelectionModal;
